import { Col, Row } from 'antd';
import classNames from 'classnames';
import React, { ReactNode } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import { AnyAction } from 'redux';

import looplineLogo from '~src/images/logos/lls-gradient-142x50.png';
import { routes } from '~src/routingConfig';
import { isNewPageDesign } from '~src/services/login';
import { getBrowserPreferredSystemLocale } from '~src/services/session';

import {
  HeaderContainer,
  HeaderImg,
  LoginLink,
  LoginSecondaryButton,
  LogoLink,
  MsgContainer,
  MsgWrapper,
  StyledHeader,
} from './header.sc';

let signUpRoutes: { [key: string]: string } = {
  de: 'https://meetings-eu1.hubspot.com/laura-heggen',
  en: 'https://meetings-eu1.hubspot.com/laura-heggen',
};

// Workaround to have the dev signup link not the website signup in dev mode
if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
  signUpRoutes = {
    de: '/signup',
    en: '/signup',
  };
}

interface OwnProps extends Pick<RouteComponentProps<any>, 'history'> {
  isAuthenticated: boolean;
  locationPath: string;
  logout(): AnyAction;
}

type Props = OwnProps & WithTranslation;

interface State {
  location: string;
}

enum HeaderType {
  goToSignUp = 'goToSignUp',
  goToSignIn = 'goToSignIn',
  logoOnly = 'logoOnly',
  none = 'none',
}

class Header extends React.Component<Props, State> {
  headerType: HeaderType | null = null;

  getInnerContent = (msgKey: string, btn: ReactNode) => (
    <MsgWrapper>
      <MsgContainer data-translation={msgKey}>{this.props.t(msgKey)}</MsgContainer>
      {btn}
    </MsgWrapper>
  );

  renderContent = () => {
    const { t, locationPath } = this.props;
    let content = null;
    let btn;
    const goToLogin = () => this.props.history.push('/login');

    switch (locationPath) {
      case routes.login:
        this.headerType = HeaderType.goToSignUp;
        btn = (
          <a href={signUpRoutes[getBrowserPreferredSystemLocale()] || signUpRoutes.en}>
            <LoginSecondaryButton data-translation="x.login.header.button.message">
              {t('x.login.header.button.message')}
            </LoginSecondaryButton>
          </a>
        );
        content = this.getInnerContent('x.login.header.message', btn);
        break;
      case '/signup':
        this.headerType = HeaderType.goToSignIn;
        btn = (
          <LoginLink onClick={goToLogin} data-translation="x.signup.header.button.message">
            {t('x.signup.header.button.message')}
          </LoginLink>
        );
        content = this.getInnerContent('x.signup.header.message', btn);
        break;
      default:
        this.headerType = HeaderType.logoOnly;
        break;
    }

    if (locationPath.startsWith('/users/signup')) {
      this.headerType = HeaderType.none;
    }

    return content;
  };

  render() {
    const { t, locationPath } = this.props;
    const isNewHeader = isNewPageDesign(locationPath);
    const headerContent = this.renderContent();
    const isLogoOnly = this.headerType === HeaderType.logoOnly;

    if (this.headerType === HeaderType.none) {
      return null;
    }

    return (
      <StyledHeader
        className={classNames({
          'new-login': isNewHeader,
          'logo-only': isLogoOnly,
        })}
      >
        <HeaderContainer>
          <Row type="flex" align="middle">
            <Col span={4} offset={isNewHeader && !isLogoOnly ? 0 : 10}>
              <LogoLink to="/">
                <HeaderImg $newLogin={isNewHeader} src={looplineLogo} />
              </LogoLink>
              {this.props.isAuthenticated && (
                <button type="button" onClick={this.props.logout}>
                  {t('x.sidebar.logout.tooltip.title')}
                </button>
              )}
            </Col>
            {isNewHeader && !isLogoOnly && (
              <Col lg={{ offset: 10, span: 10 }} xs={{ offset: 4, span: 16 }}>
                {headerContent}
              </Col>
            )}
          </Row>
        </HeaderContainer>
      </StyledHeader>
    );
  }
}

const HeaderTranslated = withTranslation()(Header);
export default HeaderTranslated;
